import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTable, useSortBy } from 'react-table';
import logoEthereum from '../assets/logo-ethereum.png';
import logoMatic from '../assets/logo-matic.png';
import logoOpensea from '../assets/logo-opensea.png';
import collectionSlugs from '../data/collectionSlugs';
import '../styles/styles.css';
import '../styles/Skeleton.css';

// ─── Constants ───────────────────────────────────────────────────────────────
const CACHE_KEY           = 'collections_cache_v2';
const CACHE_TIMESTAMP_KEY = 'collections_cache_timestamp_v2';
const CACHE_DURATION      = 5 * 60 * 1000; // 5 min
const MIN_COLLECTIONS     = 20;
const CONCURRENCY         = 3;   // parallel slugs per batch
const BATCH_DELAY_MS      = 300; // ms between batches

// ─── Helpers (module-level, no closure issues) ────────────────────────────────
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

const ALL_SLUGS = collectionSlugs.flatMap((c) => c.slugs);

const getCategoryMapping = () => {
  const mapping = {};
  collectionSlugs.forEach((cat) => {
    cat.slugs.forEach((slug) => { mapping[slug] = cat.category; });
  });
  return mapping;
};
const CATEGORY_MAP = getCategoryMapping();

const transformOne = (collection) => {
  const floorPrice  = parseFloat(collection.stats?.total?.floor_price || 0);
  const totalSupply = parseInt(collection.total_supply || 0, 10);
  const marketCap   = floorPrice * totalSupply;
  return {
    category:        CATEGORY_MAP[collection.collection] || 'Uncategorized',
    collection:      collection.name || 'Unknown Collection',
    imageUrl:        collection.image_url || 'https://via.placeholder.com/150',
    totalOwners:     parseInt(collection.stats?.total?.num_owners || 0, 10),
    totalSupply,
    totalVolume:     parseFloat(collection.stats?.total?.volume || 0),
    marketCap,
    floorPrice,
    contractAddress: collection.contracts?.[0]?.address || 'N/A',
    chain:           collection.contracts?.[0]?.chain   || 'Unknown',
    openseaUrl:      collection.opensea_url || '#',
    slug:            collection.collection || 'unknown-slug',
    description:     collection.description      || null,
    twitterUsername: collection.twitter_username  || null,
    websiteUrl:      collection.website_url       || null,
    discordUrl:      collection.discord_url       || null,
    bannerImageUrl:  collection.banner_image_url  || null,
  };
};

const transformAll = (collections) => collections.map(transformOne);

// ─── Component ────────────────────────────────────────────────────────────────
const CollectionsTable = ({ account }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Per-wallet localStorage key — null when no wallet is connected
  const watchlistKey = account ? `watchlist_${account.toLowerCase()}` : null;

  const [data,            setData]           = useState([]);
  const [filteredData,    setFilteredData]   = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(
    () => searchParams.get('category') || 'All'
  );
  const [loading,         setLoading]        = useState(true);
  const [filtersOpen,     setFiltersOpen]    = useState(false);
  const [searchQuery,     setSearchQuery]    = useState(
    () => searchParams.get('q') || ''
  );
  const [lastUpdated,     setLastUpdated]    = useState(null);
  const [error,           setError]          = useState(null);
  const [ethPrice,        setEthPrice]       = useState(null);
  const [fetchedCount,    setFetchedCount]   = useState(0);
  const totalCount = ALL_SLUGS.length;

  // ── Watchlist (per wallet, keyed by address) ──
  const [watchlist, setWatchlist] = useState(() => {
    if (!watchlistKey) return new Set();
    try { return new Set(JSON.parse(localStorage.getItem(watchlistKey) || '[]')); }
    catch { return new Set(); }
  });

  // ── Alerts ──
  const [alerts,      setAlerts]      = useState(() => {
    try { return JSON.parse(localStorage.getItem('alerts_v1') || '[]'); }
    catch { return []; }
  });
  const [alertNotice, setAlertNotice] = useState(null);

  const searchRef       = useRef(null);
  const isFetchingRef   = useRef(false);
  const rawAccRef       = useRef([]);  // accumulates raw API results during progressive load

  // ── Keyboard shortcut ──
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    const handler = () => { if (window.innerWidth > 768) setFiltersOpen(false); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── ETH price ──
  useEffect(() => {
    fetch('/api/eth-price')
      .then((r) => r.json())
      .then((d) => setEthPrice(d.usd))
      .catch(() => {});
  }, []);

  // ── Re-read alerts from localStorage when window regains focus ──
  // (detail page may have set/removed alerts while the table was mounted in background)
  useEffect(() => {
    const onFocus = () => {
      try { setAlerts(JSON.parse(localStorage.getItem('alerts_v1') || '[]')); }
      catch { setAlerts([]); }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // ── Auto-dismiss alert notice after 8s ──
  useEffect(() => {
    if (!alertNotice) return;
    const t = setTimeout(() => setAlertNotice(null), 8000);
    return () => clearTimeout(t);
  }, [alertNotice]);

  // ── Reload watchlist when wallet connects / switches / disconnects ──
  useEffect(() => {
    if (!watchlistKey) {
      setWatchlist(new Set());
      setSelectedCategory((prev) => prev === 'Watchlist' ? 'All' : prev);
      return;
    }
    try { setWatchlist(new Set(JSON.parse(localStorage.getItem(watchlistKey) || '[]'))); }
    catch { setWatchlist(new Set()); }
  }, [watchlistKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Watchlist toggle ──
  const toggleWatchlist = useCallback((slug) => {
    if (!watchlistKey) return;
    setWatchlist((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      localStorage.setItem(watchlistKey, JSON.stringify([...next]));
      return next;
    });
  }, [watchlistKey]);

  // ── Check alerts against current data ──
  const checkAlerts = useCallback((rows) => {
    const stored = JSON.parse(localStorage.getItem('alerts_v1') || '[]');
    if (!stored.length) return;
    const triggered = [];
    const remaining = stored.filter((alert) => {
      const row = rows.find((r) => r.slug === alert.slug);
      if (!row) return true;
      const hit = alert.direction === 'below'
        ? row.floorPrice > 0 && row.floorPrice < alert.threshold
        : row.floorPrice > alert.threshold;
      if (hit) { triggered.push({ ...alert, floor: row.floorPrice }); return false; }
      return true;
    });
    if (!triggered.length) return;
    localStorage.setItem('alerts_v1', JSON.stringify(remaining));
    setAlerts(remaining);

    // Append to history (max 20, newest first)
    try {
      const prev = JSON.parse(localStorage.getItem('alerts_history') || '[]');
      const next = [
        ...triggered.map((a) => ({
          name:      a.name,
          slug:      a.slug,
          imageUrl:  a.imageUrl,
          direction: a.direction,
          threshold: a.threshold,
          floor:     a.floor,
          firedAt:   new Date().toISOString(),
        })),
        ...prev,
      ].slice(0, 20);
      localStorage.setItem('alerts_history', JSON.stringify(next));
    } catch (_) {}

    triggered.forEach((a) => {
      const msg = `${a.name}: floor is ${a.floor.toFixed(3)} ETH (alert: ${a.direction} ${a.threshold} ETH)`;
      setAlertNotice(msg);
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Mint Debut Alert', { body: msg, icon: a.imageUrl });
      }
    });
  }, []);

  // ── Progressive fetch ──
  // mode='foreground': table fills in live as each collection arrives
  // mode='background': silently re-fetches, updates state all-at-once when done
  const fetchProgressively = useCallback(async (mode = 'foreground') => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    rawAccRef.current = [];

    const fresh = [];

    for (let i = 0; i < ALL_SLUGS.length; i += CONCURRENCY) {
      const batch = ALL_SLUGS.slice(i, i + CONCURRENCY);

      const results = await Promise.allSettled(
        batch.map((slug) =>
          fetch(`/api/collection?slug=${encodeURIComponent(slug)}`)
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
        )
      );

      for (const result of results) {
        if (result.status === 'fulfilled') {
          const raw = result.value;
          fresh.push(raw);
          if (mode === 'foreground') {
            setData((prev) => [...prev, transformOne(raw)]);
            setLoading(false);
            setFetchedCount(fresh.length);
          }
        }
      }

      if (i + CONCURRENCY < ALL_SLUGS.length) await delay(BATCH_DELAY_MS);
    }

    if (fresh.length >= MIN_COLLECTIONS) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(fresh));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      setLastUpdated(Date.now());
      if (mode === 'background') {
        setData(transformAll(fresh));
        checkAlerts(transformAll(fresh));
      }
    } else if (mode === 'foreground' && fresh.length === 0) {
      setError('Failed to load collections. Please refresh.');
      setLoading(false);
    }

    if (mode === 'foreground') {
      setFetchedCount(fresh.length);
      checkAlerts(transformAll(fresh));
    }
    isFetchingRef.current = false;
  }, [checkAlerts]);

  // ── Cache check + bootstrap ──
  useEffect(() => {
    const cachedRaw       = localStorage.getItem(CACHE_KEY);
    const cacheTimestamp  = localStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (cachedRaw && cacheTimestamp) {
      try {
        const collections = JSON.parse(cachedRaw);
        if (collections.length >= MIN_COLLECTIONS) {
          const age = Date.now() - parseInt(cacheTimestamp, 10);
          setData(transformAll(collections));
          setFetchedCount(collections.length);
          setLoading(false);
          setLastUpdated(parseInt(cacheTimestamp, 10));

          if (age > CACHE_DURATION) {
            // Stale: show cache immediately, refresh silently
            fetchProgressively('background');
          }
          return;
        }
      } catch (_) {}
    }

    // No valid cache — fetch progressively so the user sees data ASAP
    fetchProgressively('foreground');
  }, [fetchProgressively]);

  // ── Filter ──
  useEffect(() => {
    let filtered = data;
    if (selectedCategory === 'Watchlist') {
      filtered = filtered.filter((item) => watchlist.has(item.slug));
    } else if (selectedCategory !== 'All') {
      filtered = filtered.filter((item) => item.category === selectedCategory);
    }
    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => item.collection.toLowerCase().includes(q));
    }
    setFilteredData(filtered);
  }, [data, selectedCategory, searchQuery, watchlist]);

  // ── Format helpers ──
  const formatUSD = (ethValue, price) => {
    if (!price || typeof ethValue !== 'number' || isNaN(ethValue) || ethValue === 0) return '—';
    const usd = ethValue * price;
    if (usd >= 1e9) return `$${(usd / 1e9).toFixed(2)}B`;
    if (usd >= 1e6) return `$${(usd / 1e6).toFixed(2)}M`;
    if (usd >= 1e3) return `$${(usd / 1e3).toFixed(1)}K`;
    return `$${usd.toFixed(2)}`;
  };
  const formatCount = (num) => {
    if (typeof num !== 'number' || isNaN(num) || num === 0) return '—';
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // ── Columns ──
  const columns = React.useMemo(() => [
    ...(account ? [{
      id: 'star',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <button
          className={`star-btn ${watchlist.has(row.original.slug) ? 'starred' : ''}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWatchlist(row.original.slug); }}
          title={watchlist.has(row.original.slug) ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          {watchlist.has(row.original.slug) ? '★' : '☆'}
        </button>
      ),
    }] : []),
    {
      Header: 'Collection',
      accessor: 'collection',
      sortDescFirst: true,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={row.original.imageUrl}
            alt={row.original.collection}
            style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', verticalAlign: 'middle', marginRight: '10px', flexShrink: 0 }}
          />
          <Link to={`/collection/${row.original.slug}`} state={row.original}>
            {row.original.collection}
          </Link>
          {alerts.some((a) => a.slug === row.original.slug) && (
            <svg className="alert-indicator" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" title="Price alert set">
                  <path d="M8 2a4.5 4.5 0 00-4.5 4.5V10L2 11.5h12L12.5 10V6.5A4.5 4.5 0 008 2z" />
                  <path d="M6.5 12a1.5 1.5 0 003 0" />
                </svg>
          )}
        </div>
      ),
    },
    {
      Header: 'Market Cap',
      accessor: 'marketCap',
      sortType: 'basic',
      sortDescFirst: true,
      Cell: ({ value }) => formatUSD(value, ethPrice),
    },
    {
      Header: 'Total Supply',
      accessor: 'totalSupply',
      sortType: 'basic',
      sortDescFirst: true,
      Cell: ({ value }) => formatCount(value),
    },
    {
      Header: 'Floor Price',
      accessor: 'floorPrice',
      sortType: 'basic',
      sortDescFirst: true,
      Cell: ({ value }) => formatUSD(value, ethPrice),
    },
    {
      Header: 'Total Volume',
      accessor: 'totalVolume',
      sortType: 'basic',
      sortDescFirst: true,
      Cell: ({ value }) => formatUSD(value, ethPrice),
    },
    {
      Header: 'Owners',
      accessor: 'totalOwners',
      sortType: 'basic',
      sortDescFirst: true,
      Cell: ({ value }) => formatCount(value),
    },
    {
      Header: 'Contract',
      accessor: 'contractAddress',
      Cell: ({ row }) => {
        const chainUrls = {
          ethereum: 'https://etherscan.io/address/',
          matic:    'https://explorer-mainnet.maticvigil.com/address/',
        };
        return (
          <div style={{ textAlign: 'center' }}>
            <a href={`${chainUrls[row.original.chain]}${row.original.contractAddress}`} target="_blank" rel="noopener noreferrer">
              <img src={row.original.chain === 'ethereum' ? logoEthereum : logoMatic} alt={row.original.chain} />
            </a>
          </div>
        );
      },
    },
    {
      Header: 'OpenSea',
      accessor: 'slug',
      Cell: ({ value }) => (
        <a href={`https://opensea.io/collection/${value}`} target="_blank" rel="noopener noreferrer">
          <img src={logoOpensea} alt="OpenSea" />
        </a>
      ),
    },
  ], [ethPrice, watchlist, toggleWatchlist, alerts, account]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Initial sort from URL (read once on mount) ──
  const initialSortBy = React.useMemo(() => {
    const sort = searchParams.get('sort');
    const dir  = searchParams.get('dir');
    if (sort) return [{ id: sort, desc: dir !== 'asc' }];
    return [{ id: 'marketCap', desc: true }];
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = useTable(
    { columns, data: filteredData, initialState: { sortBy: initialSortBy } },
    useSortBy
  );

  // ── Sync filter/search/sort state to URL params ──
  useEffect(() => {
    const params = {};
    if (selectedCategory !== 'All') params.category = selectedCategory;
    if (searchQuery.trim())         params.q         = searchQuery.trim();
    if (state.sortBy.length) {
      params.sort = state.sortBy[0].id;
      params.dir  = state.sortBy[0].desc ? 'desc' : 'asc';
    }
    setSearchParams(params, { replace: true });
  }, [selectedCategory, searchQuery, state.sortBy, setSearchParams]);

  // ── Skeleton (shown only on first load before any row arrives) ──
  const LoadingSkeleton = () => (
    <div className="skeleton-table">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="skeleton-row">
          <div className="skeleton-collection">
            <div className="skeleton-image" />
            <div className="skeleton-cell" style={{ width: '150px' }} />
          </div>
          {[80, 60, 70, 90, 60, 40, 40, 40].map((w, j) => (
            <div key={j} className="skeleton-stat">
              <div className="skeleton-cell" style={{ width: `${w}px`, margin: '0 auto' }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    const diff    = Date.now() - lastUpdated;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours   = Math.floor(minutes / 60);
    if (hours   > 0) return `Updated ${hours}h ago`;
    if (minutes > 0) return `Updated ${minutes}m ago`;
    return `Updated ${seconds}s ago`;
  };

  const isStillLoading = loading || (fetchedCount < totalCount && fetchedCount < data.length + 5);
  const progressLabel = fetchedCount < totalCount
    ? `Loading ${fetchedCount}/${totalCount}…`
    : formatLastUpdated();

  return (
    <div className="collections-container">
      {error && (
        <div className="error-toast">
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '10px' }}>✕</button>
        </div>
      )}
      {alertNotice && (
        <div className="alert-toast">
          {alertNotice}
          <button onClick={() => setAlertNotice(null)}>✕</button>
        </div>
      )}

      <div className="about-hero">
        <div className="table-hero-top">
          <p className="about-eyebrow">Registry</p>
          <span className="last-updated">{progressLabel}</span>
        </div>
        <h1 className="about-headline">Genesis Collections.</h1>
      </div>

      {!filtersOpen && (
        <button className="filters-toggle" onClick={() => setFiltersOpen(true)}>✦ Filters ✦</button>
      )}
      <div className={`filters-overlay ${filtersOpen ? 'active' : ''}`}>
        <button className="filters-close" onClick={() => setFiltersOpen(false)}>✕</button>
        <div className="filters">
          <button onClick={() => { setSelectedCategory('All'); setFiltersOpen(false); }} className={selectedCategory === 'All' ? 'active' : ''}>All</button>
          {account && <button onClick={() => { setSelectedCategory('Watchlist'); setFiltersOpen(false); }} className={selectedCategory === 'Watchlist' ? 'active' : ''}>Watchlist</button>}
          {collectionSlugs.map((cat, i) => (
            <button key={i} onClick={() => { setSelectedCategory(cat.category); setFiltersOpen(false); }} className={selectedCategory === cat.category ? 'active' : ''}>{cat.category}</button>
          ))}
        </div>
      </div>

      <div className="controls-row">
        <div className="filters">
          <button onClick={() => setSelectedCategory('All')} className={selectedCategory === 'All' ? 'active' : ''}>All</button>
          {account && <button onClick={() => setSelectedCategory('Watchlist')} className={selectedCategory === 'Watchlist' ? 'active' : ''}>Watchlist</button>}
          {collectionSlugs.map((cat, i) => (
            <button key={i} onClick={() => setSelectedCategory(cat.category)} className={selectedCategory === cat.category ? 'active' : ''}>{cat.category}</button>
          ))}
        </div>
        <input
          ref={searchRef}
          type="text"
          placeholder="Search collections..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div className="table-container">
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map((hg, i) => (
                <tr key={`hg-${i}`} {...hg.getHeaderGroupProps()}>
                  {hg.headers.map((col) => (
                    <th
                      key={col.id}
                      {...col.getHeaderProps(col.id === 'star' ? undefined : col.getSortByToggleProps())}
                      className={[
                        col.id === 'star'        && 'col-star',
                        col.id === 'collection'  && 'col-name',
                        col.isSorted             && 'col-sorted',
                      ].filter(Boolean).join(' ') || undefined}
                    >
                      {col.render('Header')}
                      {col.isSorted && <span className={col.isSortedDesc ? 'sort-desc' : 'sort-asc'} />}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row, i) => {
                prepareRow(row);
                return (
                  <tr key={`row-${i}`} {...row.getRowProps()}>
                    {row.cells.map((cell, j) => (
                      <td
                        key={`cell-${i}-${j}`}
                        {...cell.getCellProps()}
                        className={[
                          cell.column.id === 'star'       && 'col-star',
                          cell.column.id === 'collection' && 'col-name',
                        ].filter(Boolean).join(' ') || undefined}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isStillLoading && fetchedCount < totalCount && (
            <div className="table-loading-bar">
              <div
                className="table-loading-fill"
                style={{ width: `${Math.round((fetchedCount / totalCount) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollectionsTable;
